import React from 'react';

import { adaptExternalIcon, Box, IconButton, Link, Row } from '@opendoor/bricks-next';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import type { ILpComponentMulticardFields } from 'declarations/contentful';
import { Navigation, Pagination } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';

import type { IComponentThemeOptions } from '../landing-pages-v2/shared/styles/ComponentTheme';

import styles from './CardCarouselV2.module.css';

const AdaptedArrowLeft = adaptExternalIcon(IconArrowLeft);
const AdaptedArrowRight = adaptExternalIcon(IconArrowRight);

interface ICardCarouselListProps<T> {
  id?: string;
  cards: T[];
  componentTheme: IComponentThemeOptions;
  columnNumber: number;
  isArched?: boolean;
  minHeight?: ILpComponentMulticardFields['minHeight'];
  footerLinkButtonText?: string;
  footerLink?: string;
  renderCard?: (card: T, id?: string) => React.ReactNode;
  layoutTheme?: 'Dark' | 'Light' | 'Grey';
  prependLink?: boolean;
}

const CardCarouselV2 = <T,>({
  id,
  cards,
  columnNumber,
  minHeight,
  footerLink,
  footerLinkButtonText,
  layoutTheme,
  renderCard,
  prependLink = true,
}: ICardCarouselListProps<T>) => {
  return (
    <Box
      display="block"
      pb="$16"
      className={styles.slider}
      $platform-web={{ boxSizing: 'content-box' }}
      mx="auto"
      maxWidth="$360"
    >
      <Swiper
        modules={[Navigation, Pagination]}
        pagination={{
          clickable: true,
          type: 'bullets',
          el: `.swiper-pagination-${id}`,
        }}
        navigation={{
          prevEl: `.swiper-button-previous-${id}`,
          nextEl: `.swiper-button-next-${id}`,
        }}
        slidesPerView={1.1}
        grabCursor
        watchSlidesProgress={true}
        spaceBetween={24}
        breakpoints={{
          640: {
            slidesPerView: 2,
          },
          768: {
            slidesPerView: columnNumber > 2 ? 3 : 2,
          },
          1200: {
            slidesPerView: columnNumber,
          },
        }}
      >
        {cards?.map((card, idx) => (
          <SwiperSlide key={idx} style={{ minHeight }}>
            {renderCard?.(card, id)}
          </SwiperSlide>
        ))}
      </Swiper>
      <Row justifyContent="space-between" pt="$8" alignItems="center">
        <Row gap="$2half">
          <IconButton
            size="medium"
            variant="default"
            icon={{ Component: AdaptedArrowLeft, label: 'Previous slide', size: 20 }}
            className={`swiper-button-previous swiper-button-previous-${id}`}
            type="button"
            analyticsName={`swiper-button-previous-${id}`}
          />
          <IconButton
            size="medium"
            variant="default"
            icon={{ Component: AdaptedArrowRight, label: 'Next slide', size: 20 }}
            className={`swiper-button-next swiper-button-next-${id}`}
            type="button"
            analyticsName={`swiper-button-next-${id}`}
          />
        </Row>
        {footerLink && (
          <Link
            analyticsName="cosmos-multicard-see-all-link"
            aria-label="see all link"
            variant={layoutTheme === 'Dark' ? 'secondaryInverse' : 'primary'}
            href={prependLink ? `/articles/topic/${footerLink}` : footerLink}
          >
            {footerLinkButtonText || 'See all'}
          </Link>
        )}
      </Row>
    </Box>
  );
};

export default CardCarouselV2;
